<template>
  <div>
    <BulkImportLoadingDialog
      v-model="savingToApi"
      :loading="savingToApi"
      :title="$t('memberDataSection.parchmentDelivery.bulkImport.importingTitle')"
      :message="$t('memberDataSection.parchmentDelivery.bulkImport.importingNote')"
    />
    <breadcrumb v-if="!embedded" :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <div v-if="!embedded" class="d-flex mb-4">
        <h2 class="font-weight-bolder">{{ pageTitle }}</h2>
        <v-spacer></v-spacer>
        <v-btn color="primary" outlined class="mr-2" @click="goBack">
          {{ $t('back') }}
        </v-btn>
      </div>

      <v-card v-if="detail" elevation="0" class="px-5">
        <v-card-text class="text-body-1 black--text pa-0">
          <template v-if="isSuccessView">
            <div class="px-4 py-4">
              <div class="bulk-result-progress-section">
                <span class="bulk-result-progress-title">
                  {{ $t('memberDataSection.parchmentDelivery.bulkImport.importCompleted') }}
                </span>
                <div class="bulk-result-progress-row mt-2">
                  <v-progress-linear
                    rounded
                    height="12"
                    :value="importProgressPercent"
                    color="#00BD73"
                    background-color="#e8f5e9"
                    class="bulk-result-progress-bar"
                  ></v-progress-linear>
                  <span class="bulk-result-progress-percent">{{ importProgressPercent }}%</span>
                </div>
                <p class="bulk-result-progress-status mt-2 mb-0">
                  <span class="font-weight-bold">{{ importedRecordsCount }}</span>
                  {{ $t('of') }}
                  <span class="font-weight-bold">{{ detail.totalRecordsCount }}</span>
                  {{ $t('recordsImported') }}
                </p>
              </div>

              <v-row class="bulk-result-summary-row mt-4" dense>
                <v-col
                  v-for="card in summaryCards"
                  :key="card.key"
                  cols="12"
                  sm="4"
                >
                  <div
                    class="bulk-result-summary-card"
                    :class="`bulk-result-summary-card--${card.key}`"
                  >
                    <div
                      class="bulk-result-summary-card__value"
                      :class="`bulk-result-summary-card__value--${card.key}`"
                    >
                      {{ card.count }}
                    </div>
                    <div class="bulk-result-summary-card__label">{{ card.label }}</div>
                  </div>
                </v-col>
              </v-row>

              <div class="bulk-result-filter-chips mt-5 d-flex flex-wrap">
                <button
                  v-for="chip in issueTypeFilterChips"
                  :key="chip.value"
                  type="button"
                  class="bulk-result-filter-chip"
                  :class="{
                    'bulk-result-filter-chip--active': resultFilter === chip.value,
                    [`bulk-result-filter-chip--${chip.value}`]: true,
                  }"
                  @click="resultFilter = chip.value"
                >
                  <v-icon
                    v-if="chip.icon"
                    small
                    class="bulk-result-filter-chip__icon mr-1"
                  >
                    {{ chip.icon }}
                  </v-icon>
                  {{ chip.label }} ({{ chip.count }})
                </button>
              </div>

              <div
                v-if="showBulkResolutionPanel"
                class="bulk-result-issues-alert mt-4"
              >
                <div class="d-flex align-start mb-3">
                  <v-icon color="#FFA92A" class="mr-2 mt-1">mdi-alert</v-icon>
                  <div>
                    <p class="bulk-result-issues-alert__heading mb-1">
                      {{ $t('memberDataSection.parchmentDelivery.bulkImport.issuesDetectedHeading') }}
                    </p>
                    <p class="bulk-result-issues-alert__body mb-0">
                      {{ $t('memberDataSection.parchmentDelivery.bulkImport.issuesDetectedDescription') }}
                    </p>
                  </div>
                </div>

                <p class="bulk-result-issues-alert__prompt mb-3">
                  {{ $t('memberDataSection.parchmentDelivery.bulkImport.bulkResolutionPrompt') }}
                </p>

                <div
                  class="bulk-result-bulk-resolution-options"
                  :class="{ 'bulk-result-bulk-resolution-options--kenya': isKenyaClient }"
                >
                  <label class="bulk-result-bulk-resolution-option">
                    <input
                      v-model="bulkResolutionAction"
                      type="radio"
                      name="bulk-resolution-action"
                      value="delete"
                    />
                    <div>
                      <p class="bulk-result-bulk-resolution-option__title mb-1">
                        {{ $t('memberDataSection.parchmentDelivery.bulkImport.bulkDeleteRecordsTitle') }}
                      </p>
                      <p class="bulk-result-bulk-resolution-option__body mb-0">
                        {{ $t('memberDataSection.parchmentDelivery.bulkImport.bulkDeleteRecordsDescription') }}
                      </p>
                    </div>
                  </label>

                  <label class="bulk-result-bulk-resolution-option">
                    <input
                      v-model="bulkResolutionAction"
                      type="radio"
                      name="bulk-resolution-action"
                      value="override"
                    />
                    <div>
                      <p class="bulk-result-bulk-resolution-option__title mb-1">
                        {{ $t('memberDataSection.parchmentDelivery.bulkImport.bulkOverrideFactoryTitle') }}
                      </p>
                      <p class="bulk-result-bulk-resolution-option__body mb-0">
                        {{ $t('memberDataSection.parchmentDelivery.bulkImport.bulkOverrideFactoryDescription') }}
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <v-card class="py-0 mt-4 bulk-result-table-card" elevation="1">
                <v-data-table
                  disable-pagination
                  :headers="tableHeaders"
                  :items="filteredRecords"
                  hide-default-footer
                  class="bulk-result-table bulk-result-table--clickable"
                  @click:row="onResultRowClick"
                >
                  <template v-slot:item.lotOutturn="{ item }">
                    <div class="bulk-result-lot-outturn-cell">
                      <div
                        v-if="isSuccessRow(item) || isFailedRow(item)"
                        class="d-flex align-center"
                      >
                        <span
                          class="text-body-2"
                          :class="issueFieldClass(shouldHighlightLotId(item))"
                        >
                          {{ getLotIdDisplay(item) }}
                        </span>
                      </div>

                      <div
                        class="d-flex align-center"
                        :class="{ 'mt-1': isSuccessRow(item) || isFailedRow(item) }"
                      >
                        <span
                          class="text-body-2"
                          :class="issueFieldClass(shouldHighlightOutturn(item))"
                        >
                          {{ getOutturnNumber(item) }}
                        </span>
                        <v-tooltip
                          v-if="shouldShowLotOutturnColumnTooltip(item)"
                          bottom
                          max-width="360"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="#FFA92A"
                              class="ml-1"
                            >
                              mdi-alert
                            </v-icon>
                          </template>
                          <div class="bulk-result-issue-tooltip">
                            <div class="font-weight-bold">{{ getColumnIssueTitle(item, 'lotOutturn') }}</div>
                            <div class="mt-1">{{ getColumnIssueDescription(item, 'lotOutturn') }}</div>
                          </div>
                        </v-tooltip>
                      </div>
                    </div>
                  </template>
                  <template v-slot:item.grower="{ item }">
                    <div class="d-flex align-center">
                      <div
                        class="bulk-result-grower-cell"
                        :class="{ 'bulk-result-field--error': shouldHighlightGrower(item) }"
                      >
                        <div v-if="getGrowerCode(item)" class="text-body-2">{{ getGrowerCode(item) }}</div>
                        <div v-if="getGrowerName(item)" class="text-body-2">{{ getGrowerName(item) }}</div>
                        <span v-if="!getGrowerCode(item) && !getGrowerName(item)">—</span>
                      </div>
                      <v-tooltip
                        v-if="shouldShowGrowerIssueTooltip(item)"
                        bottom
                        max-width="360"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            small
                            color="#FFA92A"
                            class="ml-1 flex-shrink-0"
                          >
                            mdi-alert
                          </v-icon>
                        </template>
                        <div class="bulk-result-issue-tooltip">
                          <div class="font-weight-bold">{{ getColumnIssueTitle(item, 'grower') }}</div>
                          <div class="mt-1">{{ getColumnIssueDescription(item, 'grower') }}</div>
                        </div>
                      </v-tooltip>
                    </div>
                  </template>
                  <template v-slot:item.coffeeType="{ item }">
                    {{ getPayloadField(item, "coffeeType") }}
                  </template>
                  <template v-slot:item.weights="{ item }">
                    <div class="d-flex align-center">
                      <div
                        class="bulk-result-weights-cell"
                        :class="issueFieldClass(shouldHighlightWeightsCell(item))"
                      >
                        <div class="text-body-2">
                          {{ formatWeight(getGrossWeight(item)) }}
                        </div>
                        <div class="text-body-2">
                          {{ formatWeight(getNetWeight(item)) }}
                        </div>
                      </div>
                      <v-tooltip
                        v-if="shouldShowWeightsColumnTooltip(item)"
                        bottom
                        max-width="360"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            small
                            color="#FFA92A"
                            class="ml-1 flex-shrink-0"
                          >
                            mdi-alert
                          </v-icon>
                        </template>
                        <div class="bulk-result-issue-tooltip">
                          <div class="font-weight-bold">{{ getColumnIssueTitle(item, 'weights') }}</div>
                          <div class="mt-1">{{ getColumnIssueDescription(item, 'weights') }}</div>
                        </div>
                      </v-tooltip>
                    </div>
                  </template>
                  <template v-slot:item.status="{ item }">
                    <v-btn
                      v-if="isFailedRow(item)"
                      small
                      rounded
                      depressed
                      min-width="140"
                      height="34"
                      class="bulk-result-status-btn red-button non-clickable"
                    >
                      {{ statusLabel(item) }}
                    </v-btn>

                    <v-tooltip
                      v-else-if="shouldShowStatusIssueTooltip(item)"
                      bottom
                      max-width="360"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          small
                          rounded
                          depressed
                          min-width="140"
                          height="34"
                          :class="[statusButtonClass(item), 'bulk-result-status-btn']"
                          v-bind="attrs"
                          v-on="on"
                        >
                          {{ statusLabel(item) }}
                        </v-btn>
                      </template>
                      <div class="bulk-result-issue-tooltip">
                        <div class="font-weight-bold">{{ getRecordIssueTitle(item) }}</div>
                        <div class="mt-1">{{ getRecordIssueDescription(item) }}</div>
                      </div>
                    </v-tooltip>

                    <v-btn
                      v-else
                      small
                      rounded
                      depressed
                      min-width="140"
                      height="34"
                      :class="[statusButtonClass(item), 'bulk-result-status-btn']"
                    >
                      {{ statusLabel(item) }}
                    </v-btn>
                  </template>
                  <template v-slot:item.action="{ item }">
                    <div class="d-flex align-center justify-center flex-nowrap bulk-result-actions">
                      <template v-if="isFailedRow(item)">
                        <v-btn
                          icon
                          small
                          elevation="2"
                          class="mr-1"
                          @click.stop="downloadFailedRecord(item)"
                        >
                          <v-icon small>mdi-download</v-icon>
                        </v-btn>
                        <v-btn icon small elevation="2" @click.stop="promptDeleteRecord(item)">
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </template>
                      <template v-else>
                        <v-btn icon small elevation="2" class="mr-1" @click.stop="editRecord(item)">
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn icon small elevation="2" @click.stop="promptDeleteRecord(item)">
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </template>
                    </div>
                  </template>
                </v-data-table>
              </v-card>

              <div
                v-if="showBulkResolutionPanel"
                class="bulk-result-bulk-actions-row mt-4 d-flex justify-end align-center"
              >
                <v-btn
                  outlined
                  height="40"
                  min-width="140"
                  class="bulk-result-bulk-cancel-btn text-uppercase font-weight-bold mr-3"
                  :class="{ 'bulk-result-bulk-cancel-btn--kenya': isKenyaClient }"
                  :color="isKenyaClient ? undefined : 'primary'"
                  @click="onBulkResolutionCancel"
                >
                  {{ $t('cancel') }}
                </v-btn>
                <v-btn
                  depressed
                  height="40"
                  min-width="140"
                  class="bulk-result-bulk-continue-btn text-uppercase font-weight-bold white--text"
                  :class="{ 'bulk-result-bulk-continue-btn--kenya': isKenyaClient }"
                  :color="isKenyaClient ? undefined : 'primary'"
                  :disabled="savingToApi"
                  :loading="savingToApi"
                  @click="onBulkResolutionContinue"
                >
                  {{ $t('continue') }}
                </v-btn>
              </div>

              <!-- All issues resolved (or the file was clean): the discrepancy panel is
                   hidden, so surface a save button to actually import the records. -->
              <div
                v-if="showCleanSavePanel"
                class="bulk-result-bulk-actions-row mt-4 d-flex justify-end align-center"
              >
                <v-btn
                  outlined
                  height="40"
                  min-width="140"
                  class="bulk-result-bulk-cancel-btn text-uppercase font-weight-bold mr-3"
                  :class="{ 'bulk-result-bulk-cancel-btn--kenya': isKenyaClient }"
                  :color="isKenyaClient ? undefined : 'primary'"
                  :disabled="savingToApi"
                  @click="onBulkResolutionCancel"
                >
                  {{ $t('cancel') }}
                </v-btn>
                <v-btn
                  depressed
                  height="40"
                  min-width="140"
                  class="bulk-result-bulk-continue-btn text-uppercase font-weight-bold white--text"
                  :class="{ 'bulk-result-bulk-continue-btn--kenya': isKenyaClient }"
                  :color="isKenyaClient ? undefined : 'primary'"
                  :disabled="savingToApi"
                  :loading="savingToApi"
                  @click="onConfirmSaveImport"
                >
                  {{ $t('memberDataSection.parchmentDelivery.bulkImport.saveImport') }}
                </v-btn>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="px-4 py-4">
              <p class="red--text font-weight-bold text-h6 mb-2">
                {{ $t('memberDataSection.parchmentDelivery.bulkImport.importFailed') }}
              </p>
              <p class="text-body-2 mb-4">{{ errorIntro }}</p>

              <v-card v-if="displayRecords.length" class="py-4" elevation="1" style="max-height: 480px; overflow: auto;">
                <v-data-table
                  disable-pagination
                  :headers="errorHeaders"
                  :items="displayRecords"
                  hide-default-footer
                >
                  <template v-slot:item.outturnNumber="{ item }">
                    {{ getOutturnNumber(item) }}
                  </template>
                  <template v-slot:item.message="{ item }">
                    {{ item.message || item.errorMessage || '—' }}
                  </template>
                  <template v-slot:item.status="{ item }">
                    <v-chip style="width: 100px;" class="justify-center" outlined color="error">
                      {{ item.status || 'FAILED' }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-card>

              <v-divider class="my-5 px-0"></v-divider>
              <v-card-actions class="px-0">
                <v-spacer></v-spacer>
                <v-btn outlined color="primary" class="mr-2" width="112" @click="retryImport">
                  {{ $t('memberDataSection.parchmentDelivery.bulkImport.tryAgain') }}
                </v-btn>
                <v-btn color="primary" width="112" class="btn-11 btn-22 white--text" @click="goBack">
                  {{ $t('back') }}
                </v-btn>
              </v-card-actions>
            </div>
          </template>
        </v-card-text>
      </v-card>

      <v-card v-else elevation="0" class="px-5 pa-8 text-center text--secondary">
        {{ $t('memberDataSection.parchmentDelivery.noDataFound') }}
        <div class="mt-4">
          <v-btn color="primary" outlined @click="retryImport">{{ $t('memberDataSection.parchmentDelivery.bulkImport.tryAgain') }}</v-btn>
        </div>
      </v-card>
    </v-container>

    <ParchmentDeliveryBulkImportSuccessDialog
      :dialog="bulkImportSuccessDialog"
      :record-count="bulkImportSuccessCount"
      @close="onBulkImportSuccessClose"
    />

    <ParchmentDeliveryBulkDeleteConfirmDialog
      :dialog="deleteDiscrepancyConfirmDialog"
      :record-count="bulkActionScopeRecords.length"
      :issue-type-label="bulkDeleteIssueTypeLabel"
      @cancel:action="onCancelBulkDelete"
      @continue:action="onConfirmBulkDelete"
    />

    <ParchmentDeliveryOverwriteConfirmDialog
      :dialog="bulkOverrideConfirmDialog"
      @cancel:action="onCancelBulkOverride"
      @continue:action="onConfirmBulkOverride"
    />

    <ParchmentDeliveryBulkDeleteConfirmDialog
      :dialog="deleteRecordConfirmDialog"
      single-record
      @cancel:action="onCancelDeleteRecord"
      @continue:action="onConfirmDeleteRecord"
    />

    <ParchmentDeliveryDetailDialog
      v-model="recordDetailDialogVisible"
      :record="selectedDetailRecord"
      @edit="onDetailEdit"
      @resolve="onDetailResolve"
      @close="onDetailClose"
    />

    <ParchmentDeliveryEditDialog
      v-model="editDialogVisible"
      :record="editingRecord"
      @save="onEditSave"
      @close="onEditClose"
    />
  </div>
</template>

<script>
import DetailDialog from "../DetailDialog.vue";
import EditDialog from "../EditDialog.vue";
import OverwriteConfirmDialog from "./OverwriteConfirmDialog.vue";
import BulkDeleteConfirmDialog from "./BulkDeleteConfirmDialog.vue";
import BulkImportSuccessDialog from "./BulkImportSuccessDialog.vue";
import BulkImportLoadingDialog from "./BulkImportLoadingDialog.vue";
import { isKenyaClient } from "@/utils";
import {
  BULK_RESULT_STORAGE_KEY,
  BULK_RECORD_STATUS,
  BULK_ISSUE_TYPE,
  BULK_ISSUE_I18N_KEYS,
  isDataDiscrepancyRecord,
  isSuccessRecord,
  isFailedRecord,
  isLotOutturnColumnIssue,
  isGrowerRelatedIssue,
  isWeightsColumnIssue,
  resolveBulkIssueType,
  getResolvableFieldConflicts,
  mergeResolutionIntoPayload,
  isHardBlockRecord,
  isOverrideEligibleRecord,
} from "@/utils/parchmentDeliveryBulkImport";
import ParchmentDeliveryService from "@/_services/ParchmentDeliveryService";

const RESULT_FILTER = {
  ALL: "all",
  SUCCESS: "success",
};

const BULK_RESOLUTION_ACTION = {
  DELETE: "delete",
  OVERRIDE: "override",
};

/** Display order for per-issue-type filter chips */
const ISSUE_TYPE_FILTER_ORDER = [
  BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH,
  BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER,
  BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER,
  BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH,
  BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS,
  BULK_ISSUE_TYPE.WEIGHT_MISMATCH,
  BULK_ISSUE_TYPE.MISSING_WEIGHT_VALUE,
  BULK_ISSUE_TYPE.DELIVERY_DATE_MISMATCH,
  BULK_ISSUE_TYPE.BAGS_QUANTITY_MISMATCH,
];

export default {
  name: "ParchmentDeliveryBulkImportResult",
  components: {
    ParchmentDeliveryDetailDialog: DetailDialog,
    ParchmentDeliveryEditDialog: EditDialog,
    ParchmentDeliveryOverwriteConfirmDialog: OverwriteConfirmDialog,
    ParchmentDeliveryBulkDeleteConfirmDialog: BulkDeleteConfirmDialog,
    ParchmentDeliveryBulkImportSuccessDialog: BulkImportSuccessDialog,
    BulkImportLoadingDialog,
  },
  props: {
    mode: {
      type: String,
      default: "success",
      validator: (v) => ["success", "error"].includes(v),
    },
    importData: {
      type: Object,
      default: null,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      detail: null,
      resultFilter: RESULT_FILTER.ALL,
      bulkResolutionAction: BULK_RESOLUTION_ACTION.OVERRIDE,
      deleteDiscrepancyConfirmDialog: false,
      bulkOverrideConfirmDialog: false,
      bulkImportSuccessDialog: false,
      bulkImportSuccessCount: 0,
      deleteRecordConfirmDialog: false,
      savingToApi: false,
      hasImported: false,
      pendingDeleteRecord: null,
      recordDetailDialogVisible: false,
      selectedDetailRecord: null,
      editDialogVisible: false,
      editingRecord: null,
      tableHeaders: [
        { text: this.$t("memberDataSection.parchmentDelivery.bulkImport.lotIdAndOutturn"), value: "lotOutturn", class: "bulk-result-table-header" },
        { text: this.$t("memberDataSection.parchmentDelivery.bulkImport.grower"), value: "grower", class: "bulk-result-table-header" },
        { text: this.$t("memberDataSection.parchmentDelivery.bulkImport.coffeeType"), value: "coffeeType", class: "bulk-result-table-header" },
        { text: this.$t("memberDataSection.parchmentDelivery.bulkImport.grossAndNetWeight"), value: "weights", class: "bulk-result-table-header" },
        { text: this.$t("oma.status"), value: "status", align: "center", class: "bulk-result-table-header" },
        { text: this.$t("oma.action"), value: "action", align: "center", sortable: false, class: "bulk-result-table-header bulk-result-action-col", width: "120px" },
      ],
      errorHeaders: [
        { text: this.$t("memberDataSection.parchmentDelivery.outturnNumber"), value: "outturnNumber", class: "black--text" },
        { text: this.$t("oma.status"), value: "status", align: "center", class: "black--text" },
        { text: this.$t("memberDataSection.parchmentDelivery.bulkImport.errorDetails"), value: "message", class: "black--text" },
      ],
    };
  },
  computed: {
    isKenyaClient() {
      return isKenyaClient();
    },
    isSuccessView() {
      return this.mode === "success";
    },
    pageTitle() {
      return this.isSuccessView
        ? this.$t("memberDataSection.parchmentDelivery.bulkImport.title")
        : this.$t("memberDataSection.parchmentDelivery.bulkImport.importFailed");
    },
    breadcrumbs() {
      return [
        { text: this.$t("dashboard"), disabled: false, to: { name: "AdminDashboard" }, exact: true },
        { text: this.$t("dashboardSection.cropOverview"), disabled: true, href: "#" },
        { text: this.$t("memberDataSection.buyingStation"), disabled: false, to: { name: "CoffeeBuyingStation" }, exact: true },
        {
          text: this.$t("memberDataSection.parchmentDelivery.listTitle"),
          disabled: false,
          to: { name: "CoffeeBuyingStationParchmentDeliveries" },
          exact: true,
        },
        { text: this.pageTitle, disabled: true, href: "#" },
      ];
    },
    allRecords() {
      return this.detail?.records || [];
    },
    successRecords() {
      return this.allRecords.filter((r) => isSuccessRecord(r));
    },
    failedRecords() {
      return this.allRecords.filter((r) => isFailedRecord(r));
    },
    discrepancyRecords() {
      return this.allRecords.filter((r) => isDataDiscrepancyRecord(r));
    },
    importedRecordsCount() {
      if (this.detail?.importedRecordsCount != null) {
        return this.detail.importedRecordsCount;
      }
      return this.successRecords.length + this.discrepancyRecords.length;
    },
    importProgressPercent() {
      const total = this.detail?.totalRecordsCount || 0;
      if (!total) return 0;
      return Math.min(100, Math.round((this.importedRecordsCount / total) * 100));
    },
    totalRecordsCount() {
      return this.detail?.totalRecordsCount || 0;
    },
    successRecordsCount() {
      return this.detail?.successRecordsCount ?? this.successRecords.length;
    },
    issuesRecordsCount() {
      return this.detail?.discrepancyRecordsCount ?? this.discrepancyRecords.length;
    },
    summaryCards() {
      const t = (key) => this.$t(`memberDataSection.parchmentDelivery.bulkImport.${key}`);
      return [
        { key: "total", label: t("tabTotalRecords"), count: this.totalRecordsCount },
        { key: "success", label: t("tabSuccess"), count: this.successRecordsCount },
        { key: "issues", label: t("tabIssues"), count: this.issuesRecordsCount },
      ];
    },
    issueTypeCounts() {
      const counts = {};
      ISSUE_TYPE_FILTER_ORDER.forEach((type) => { counts[type] = 0; });
      this.allRecords.forEach((r) => {
        if (!isDataDiscrepancyRecord(r)) return;
        // Count the primary issueType only (for filter chips)
        if (r.issueType) {
          counts[r.issueType] = (counts[r.issueType] || 0) + 1;
        }
      });
      return counts;
    },
    issueTypeFilterChips() {
      const t = (key) => this.$t(`memberDataSection.parchmentDelivery.bulkImport.${key}`);
      const chips = [
        {
          value: RESULT_FILTER.ALL,
          label: t("filterAll"),
          count: this.totalRecordsCount,
          icon: "mdi-check",
        },
        {
          value: RESULT_FILTER.SUCCESS,
          label: t("filterSuccess"),
          count: this.successRecordsCount,
          icon: "mdi-check",
        },
      ];
      ISSUE_TYPE_FILTER_ORDER.forEach((issueType) => {
        const count = this.issueTypeCounts[issueType] || 0;
        if (count === 0) return;
        const labelKey = BULK_ISSUE_I18N_KEYS[issueType]?.filterLabel;
        chips.push({
          value: issueType,
          label: labelKey ? t(labelKey) : issueType,
          count,
          icon: "mdi-alert",
        });
      });
      return chips;
    },
    showBulkResolutionPanel() {
      return this.issuesRecordsCount > 0 && this.resultFilter !== RESULT_FILTER.SUCCESS;
    },
    // Reveal save/import actions once there are no discrepancies left (all resolved,
    // or the file was clean) so records can be committed — bulk Continue is gone by then.
    showCleanSavePanel() {
      return (
        Boolean(this.importData) &&
        !this.hasImported &&
        this.isSuccessView &&
        this.issuesRecordsCount === 0 &&
        this.successRecordsCount > 0
      );
    },
    bulkActionScopeRecords() {
      if (this.resultFilter === RESULT_FILTER.ALL) {
        return this.discrepancyRecords;
      }
      if (this.resultFilter === RESULT_FILTER.SUCCESS) {
        return [];
      }
      return this.discrepancyRecords.filter((r) => r.issueType === this.resultFilter);
    },
    bulkResolvableScopeRecords() {
      // Only override-eligible: outturn found in DB AND delivery date matches DB record.
      // Soft-issue records (grower/weight mismatch) qualify when delivery date also matches.
      return this.bulkActionScopeRecords.filter((r) => isOverrideEligibleRecord(r));
    },
    bulkHardBlockRecords() {
      return this.bulkActionScopeRecords.filter((r) => isHardBlockRecord(r));
    },
    bulkDeleteIssueTypeLabel() {
      if (this.resultFilter === RESULT_FILTER.ALL) {
        return "";
      }
      const base = "memberDataSection.parchmentDelivery.bulkImport";
      const labelKey = BULK_ISSUE_I18N_KEYS[this.resultFilter]?.filterLabel;
      return labelKey ? this.$t(`${base}.${labelKey}`) : "";
    },
    filteredRecords() {
      if (this.resultFilter === RESULT_FILTER.ALL) {
        return this.allRecords;
      }
      if (this.resultFilter === RESULT_FILTER.SUCCESS) {
        return this.successRecords;
      }
      return this.allRecords.filter(
        (r) => isDataDiscrepancyRecord(r) && r.issueType === this.resultFilter
      );
    },
    displayRecords() {
      const records = this.detail?.records || [];
      if (records.length) return records;
      if (this.detail?.templateError && this.detail?.message) {
        return [
          {
            id: "template",
            status: BULK_RECORD_STATUS.FAILED,
            message: this.detail.message,
            payloadJsonData: {},
          },
        ];
      }
      return [];
    },
    errorIntro() {
      if (this.detail?.templateError) {
        return (
          this.detail.message ||
          this.$t("memberDataSection.parchmentDelivery.bulkImport.templateErrorIntro")
        );
      }
      return this.$t("memberDataSection.parchmentDelivery.bulkImport.importFailedIntro");
    },
  },
  watch: {
    importData(val) {
      if (val) {
        this.detail = val;
        this.resultFilter = "all";
      }
    },
    resultFilter() {
      if (this.deleteDiscrepancyConfirmDialog) {
        this.deleteDiscrepancyConfirmDialog = false;
      }
      if (this.bulkOverrideConfirmDialog) {
        this.bulkOverrideConfirmDialog = false;
      }
    },
  },
  mounted() {
    this.loadDetail();
  },
  methods: {
    loadDetail() {
      if (this.importData) {
        this.detail = this.importData;
        return;
      }
      try {
        const raw = sessionStorage.getItem(BULK_RESULT_STORAGE_KEY);
        this.detail = raw ? JSON.parse(raw) : null;
      } catch (e) {
        this.detail = null;
      }
    },
    isSuccessRow(item) {
      return isSuccessRecord(item);
    },
    isFailedRow(item) {
      return isFailedRecord(item);
    },
    persistDetail() {
      if (!this.detail) return;
      try {
        sessionStorage.setItem(BULK_RESULT_STORAGE_KEY, JSON.stringify(this.detail));
      } catch (e) {
        console.error(e);
      }
    },
    refreshDetailCounts() {
      if (!this.detail) return;
      this.detail.totalRecordsCount = this.detail.records.length;
      this.detail.successRecordsCount = this.successRecords.length;
      this.detail.failedRecordsCount = this.failedRecords.length;
      this.detail.discrepancyRecordsCount = this.discrepancyRecords.length;
      this.detail.importedRecordsCount = this.importedRecordsCount;
    },
    getPayloadField(item, field) {
      const p = item?.payloadJsonData || item?.payload || {};
      const val = p[field];
      return val != null && val !== "" ? val : "—";
    },
    getOutturnNumber(item) {
      return this.getPayloadField(item, "outturnNumber");
    },
    getLotIdDisplay(item) {
      return this.getPayloadField(item, "id");
    },
    issueFieldClass(highlight) {
      return highlight ? "bulk-result-field--error" : "";
    },
    getGrossWeight(item) {
      return this.getPayloadField(item, "grossWeight");
    },
    getNetWeight(item) {
      // Prefer netWeight, fall back to netWeightWithBags.
      const net = this.getPayloadField(item, "netWeight");
      if (net !== "—") return net;
      return this.getPayloadField(item, "netWeightWithBags");
    },
    formatWeight(value) {
      if (value == null || value === "" || value === "—") return "—";
      const s = String(value).trim();
      return s ? `${s} kg` : "—";
    },
    getItemIssues(item) {
      if (item.issues && item.issues.length) return item.issues;
      if (item.issueType) return [{ issueType: item.issueType, issueContext: item.issueContext || {} }];
      return [];
    },
    shouldHighlightLotId(item) {
      if (!isSuccessRecord(item)) return false;
      return this.getItemIssues(item).some((i) => isLotOutturnColumnIssue(i.issueType));
    },
    shouldHighlightOutturn(item) {
      return this.getItemIssues(item).some((i) => isLotOutturnColumnIssue(i.issueType));
    },
    shouldShowLotOutturnColumnTooltip(item) {
      return this.shouldHighlightOutturn(item);
    },
    shouldHighlightGrower(item) {
      return this.getItemIssues(item).some((i) => isGrowerRelatedIssue(i.issueType));
    },
    shouldShowGrowerIssueTooltip(item) {
      return this.shouldHighlightGrower(item);
    },
    shouldHighlightWeightsCell(item) {
      return this.getItemIssues(item).some((i) => isWeightsColumnIssue(i.issueType));
    },
    shouldShowWeightsColumnTooltip(item) {
      return this.shouldHighlightWeightsCell(item);
    },
    shouldShowStatusIssueTooltip(item) {
      return isDataDiscrepancyRecord(item) && Boolean(resolveBulkIssueType(item));
    },
    getColumnIssue(item, columnType) {
      const issues = this.getItemIssues(item);
      return issues.find((i) => {
        if (columnType === "lotOutturn") return isLotOutturnColumnIssue(i.issueType);
        if (columnType === "grower") return isGrowerRelatedIssue(i.issueType);
        if (columnType === "weights") return isWeightsColumnIssue(i.issueType);
        return false;
      }) || null;
    },
    getColumnIssueTitle(item, columnType) {
      const issue = this.getColumnIssue(item, columnType) || { issueType: resolveBulkIssueType(item) };
      const keys = BULK_ISSUE_I18N_KEYS[issue.issueType];
      if (!keys) return "";
      return this.$t(`memberDataSection.parchmentDelivery.bulkImport.${keys.title}`);
    },
    getColumnIssueDescription(item, columnType) {
      const issue = this.getColumnIssue(item, columnType) || { issueType: resolveBulkIssueType(item), issueContext: item.issueContext || {} };
      const keys = BULK_ISSUE_I18N_KEYS[issue.issueType];
      if (!keys) return "";
      const ctx = issue.issueContext || item.issueContext || {};
      return this.$t(`memberDataSection.parchmentDelivery.bulkImport.${keys.description}`, ctx);
    },
    getRecordIssueTitle(item) {
      const issueType = resolveBulkIssueType(item);
      const keys = BULK_ISSUE_I18N_KEYS[issueType];
      if (!keys) return "";
      return this.$t(`memberDataSection.parchmentDelivery.bulkImport.${keys.title}`);
    },
    getRecordIssueDescription(item) {
      const issueType = resolveBulkIssueType(item);
      const keys = BULK_ISSUE_I18N_KEYS[issueType];
      if (!keys) return "";
      const ctx = item.issueContext || {};
      return this.$t(`memberDataSection.parchmentDelivery.bulkImport.${keys.description}`, ctx);
    },
    getGrowerCode(item) {
      const p = item?.payloadJsonData || item?.payload || {};
      return p.grower != null ? String(p.grower).trim() : "";
    },
    getGrowerName(item) {
      const p = item?.payloadJsonData || item?.payload || {};
      return p.growerName != null ? String(p.growerName).trim() : "";
    },
    statusLabel(item) {
      if (isFailedRecord(item)) {
        return this.$t("failed");
      }
      if (isDataDiscrepancyRecord(item)) {
        return this.$t("memberDataSection.parchmentDelivery.bulkImport.issuesDetected");
      }
      if (isSuccessRecord(item)) {
        return this.$t("success");
      }
      return this.$t("failed");
    },
    statusButtonClass(item) {
      if (isDataDiscrepancyRecord(item)) return "orange-button";
      if (isSuccessRecord(item)) return "green-button";
      return "red-button";
    },
    async onConfirmSaveImport() {
      const payloads = this.successRecords.map((r) => r.payloadJsonData).filter(Boolean);
      await this._callBulkImportApi(payloads);
    },
    onBulkResolutionCancel() {
      this.retryImport();
    },
    onBulkResolutionContinue() {
      if (this.bulkResolutionAction === BULK_RESOLUTION_ACTION.DELETE) {
        if (!this.bulkActionScopeRecords.length) return;
        this.deleteDiscrepancyConfirmDialog = true;
        return;
      }
      if (!this.bulkResolvableScopeRecords.length) {
        this.$notify({
          type: "info",
          title: this.$t("memberDataSection.parchmentDelivery.bulkImport.bulkOverrideFactoryTitle"),
          text: this.$t("memberDataSection.parchmentDelivery.bulkImport.bulkOverrideNoneResolvable"),
        });
        return;
      }
      this.bulkOverrideConfirmDialog = true;
    },
    onCancelBulkDelete() {
      this.deleteDiscrepancyConfirmDialog = false;
    },
    async onConfirmBulkDelete() {
      this.deleteDiscrepancyConfirmDialog = false;
      if (!this.detail?.records) return;

      // Delete path: only keep SUCCESS records + hard-block discrepancies (outturn not in DB).
      // Records with soft issues (grower/weight mismatch) where outturn+date don't match are excluded.
      // Records where outturn OR delivery date don't match DB are excluded (the "delete" action).
      const recordsToSave = this.detail.records.filter((r) => {
        if (isSuccessRecord(r)) return true;
        // Keep hard-block discrepancies out — they have no matching DB record so exclude them
        return false;
      });

      await this._callBulkImportApi(recordsToSave);
    },
    onCancelBulkOverride() {
      this.bulkOverrideConfirmDialog = false;
    },
    async onConfirmBulkOverride() {
      this.bulkOverrideConfirmDialog = false;
      if (!this.detail?.records) return;

      // Override path: SUCCESS records + override-eligible discrepancies (outturn AND date match DB).
      // For override-eligible records, use the uploaded (CSV) payload values.
      const recordsToSave = this.detail.records.reduce((acc, record) => {
        if (isSuccessRecord(record)) {
          acc.push(record.payloadJsonData);
          return acc;
        }
        if (isOverrideEligibleRecord(record)) {
          // Use uploaded values (override factory data)
          const conflicts = getResolvableFieldConflicts(record);
          const selections = {};
          conflicts.forEach((c) => { selections[c.fieldKey] = "uploaded"; });
          acc.push(mergeResolutionIntoPayload(record, selections));
          return acc;
        }
        return acc;
      }, []);

      await this._callBulkImportApi(recordsToSave);
    },
    async _callBulkImportApi(payloads) {
      if (!payloads.length) {
        this.$notify({
          type: "info",
          title: this.$t("memberDataSection.parchmentDelivery.bulkImport.importCompleted"),
          text: this.$t("memberDataSection.parchmentDelivery.bulkImport.bulkOverrideNoneResolvable"),
        });
        return;
      }

      this.savingToApi = true;
      try {
        const uploadedFile = this.detail?._uploadedFile;
        let res;
        if (uploadedFile) {
          const formData = new FormData();
          formData.append('file', uploadedFile, uploadedFile.name);
          formData.append('records', JSON.stringify(payloads));
          res = await ParchmentDeliveryService.bulkImportWithFile(formData);
        } else {
          res = await ParchmentDeliveryService.bulkImportParchmentDeliveries(payloads);
        }
        const successCount = res?.data?.successCount ?? res?.successCount ?? 0;
        const failedCount  = res?.data?.failedCount  ?? res?.failedCount  ?? 0;
        const totalCount   = res?.data?.totalCount   ?? res?.totalCount   ?? payloads.length;
        const failedResults = (res?.data?.results ?? res?.results ?? []).filter((r) => !r.success);

        if (successCount > 0) {
          // Records were saved — mark imported so the finalize button hides.
          this.hasImported = true;
        }

        if (successCount > 0 && failedCount === 0) {
          // All succeeded
          this.bulkImportSuccessCount = successCount;
          this.bulkImportSuccessDialog = true;
        } else if (successCount > 0 && failedCount > 0) {
          // Partial success — show inline notification, still open success dialog for saved ones
          const failedOutturnList = failedResults.map((r) => r.outturnNumber).filter(Boolean).join(", ");
          const failedDetail = failedResults.map((r) => `${r.outturnNumber}: ${r.message}`).join("\n");
          this.$notify({
            type: "warn",
            title: `${successCount} of ${totalCount} records saved`,
            text: failedOutturnList
              ? `Failed (${failedCount}): ${failedOutturnList}`
              : `${failedCount} record(s) could not be saved`,
            duration: 8000,
          });
          this.bulkImportSuccessCount = successCount;
          this.bulkImportSuccessDialog = true;
          console.warn("[BulkImport] Failed records:", failedDetail);
        } else {
          // All failed
          const failedDetail = failedResults.map((r) => `${r.outturnNumber}: ${r.message}`).join("\n");
          this.$notify({
            type: "error",
            title: this.$t("memberDataSection.parchmentDelivery.bulkImport.importFailed"),
            text: failedResults.length
              ? failedResults.map((r) => `${r.outturnNumber}: ${r.message}`).join("; ")
              : this.$t("error"),
            duration: 10000,
          });
          console.error("[BulkImport] All records failed:", failedDetail);
        }
      } catch (err) {
        const msg = err?.data?.message || err?.message || this.$t("error");
        this.$notify({ type: "error", title: this.$t("error"), text: msg });
      } finally {
        this.savingToApi = false;
      }
    },
    openBulkImportSuccessIfComplete() {
      if (!this.isSuccessView || this.discrepancyRecords.length > 0) {
        return false;
      }
      this.bulkImportSuccessCount = this.successRecordsCount;
      this.bulkImportSuccessDialog = true;
      return true;
    },
    onBulkImportSuccessClose() {
      this.bulkImportSuccessDialog = false;
      this.goBack();
    },
    onResultRowClick(item, slot) {
      if (isFailedRecord(item)) return;
      const target = slot?.nativeEvent?.target;
      if (target && target.closest && target.closest(".bulk-result-actions")) {
        return;
      }
      this.selectedDetailRecord = item;
      this.recordDetailDialogVisible = true;
    },
    onDetailClose() {
      this.selectedDetailRecord = null;
    },
    onDetailResolve({ record, payload, selections }) {
      if (!this.detail?.records || !record) return;
      if (record.issueType === BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH) {
        this.$notify({
          type: "error",
          title: this.$t("memberDataSection.parchmentDelivery.bulkImport.issueOutturnNumberMismatchTitle"),
          text: this.$t("memberDataSection.parchmentDelivery.bulkImport.issueOutturnNumberMismatchDescription"),
        });
        return;
      }
      const idx = this.detail.records.findIndex((r) => r.id === record.id);
      if (idx < 0) return;

      const updated = {
        ...this.detail.records[idx],
        status: BULK_RECORD_STATUS.SUCCESS,
        payloadJsonData: { ...payload },
        resolutionApplied: true,
        resolutionSelections: selections,
        recordedJsonData: this.detail.records[idx].recordedJsonData || {},
        // Clear the discrepancy markers so the row's field-level warnings
        // (red text + ⚠️) disappear — they key on issueType/issues, not status.
        issueType: null,
        issues: [],
        issueContext: {},
      };
      this.$set(this.detail.records, idx, updated);
      this.selectedDetailRecord = updated;
      this.persistDetail();
      this.refreshDetailCounts();
    },
    onDetailEdit(record) {
      this.recordDetailDialogVisible = false;
      this.editRecord(record);
    },
    editRecord(item) {
      if (!item || isFailedRecord(item)) return;
      this.editingRecord = item;
      this.editDialogVisible = true;
    },
    onEditClose() {
      this.editingRecord = null;
    },
    onEditSave({ record, payload }) {
      if (!this.detail?.records || !record) return;
      const idx = this.detail.records.findIndex((r) => r.id === record.id);
      if (idx < 0) return;

      const updated = {
        ...this.detail.records[idx],
        payloadJsonData: { ...payload },
      };
      this.$set(this.detail.records, idx, updated);
      if (this.selectedDetailRecord?.id === record.id) {
        this.selectedDetailRecord = updated;
      }
      this.persistDetail();
      this.$notify({
        type: "success",
        title: this.$t("success"),
        text: this.$t("memberDataSection.parchmentDelivery.bulkImport.editRecordSaved"),
      });
    },
    downloadFailedRecord() {
      this.$notify({
        type: "info",
        title: this.$t("memberDataSection.parchmentDelivery.bulkImport.downloadXls"),
        text: this.$t("memberDataSection.parchmentDelivery.bulkImport.downloadFailedRowPending"),
      });
    },
    promptDeleteRecord(item) {
      if (!this.detail?.records || !item) return;
      this.pendingDeleteRecord = item;
      this.deleteRecordConfirmDialog = true;
    },
    onCancelDeleteRecord() {
      this.deleteRecordConfirmDialog = false;
      this.pendingDeleteRecord = null;
    },
    onConfirmDeleteRecord() {
      this.deleteRecordConfirmDialog = false;
      const item = this.pendingDeleteRecord;
      this.pendingDeleteRecord = null;
      if (!this.detail?.records || !item) return;

      const idx = this.detail.records.findIndex((r) => r.id === item.id);
      if (idx < 0) return;

      const issueType = item.issueType;
      this.detail.records.splice(idx, 1);
      this.refreshDetailCounts();
      this.persistDetail();

      if (
        issueType &&
        this.resultFilter === issueType &&
        !this.issueTypeCounts[issueType]
      ) {
        const remaining = ISSUE_TYPE_FILTER_ORDER.find((k) => this.issueTypeCounts[k] > 0);
        this.resultFilter = remaining || RESULT_FILTER.ALL;
      }
    },
    goBack() {
      if (this.embedded) {
        this.$emit("back");
        return;
      }
      this.clearStorage();
      this.$router.push({ name: "CoffeeBuyingStationParchmentDeliveries" });
    },
    retryImport() {
      if (this.embedded) {
        this.$emit("retry");
        return;
      }
      this.$router.push({
        name: "CoffeeBuyingStationParchmentDeliveriesBulkImport",
        query: { tab: "import" },
      });
    },
    clearStorage() {
      try {
        sessionStorage.removeItem(BULK_RESULT_STORAGE_KEY);
      } catch (e) {
        /* ignore */
      }
    },
  },
  beforeDestroy() {
    if (this.$route.name === "CoffeeBuyingStation") {
      this.clearStorage();
    }
  },
};
</script>

<style lang="scss" scoped>
.bulk-result-progress-title {
  color: #9e9e9e;
  font-size: 14px;
}

.bulk-result-progress-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bulk-result-progress-bar {
  flex: 1 1 auto;
}

.bulk-result-progress-percent {
  color: #00bd73;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
  min-width: 40px;
  text-align: right;
}

.bulk-result-progress-status {
  color: #9e9e9e;
  font-size: 14px;
}

.bulk-result-summary-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 20px 20px 20px 24px;
  border-left: 6px solid transparent;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.bulk-result-summary-card--total {
  border-left-color: #607d8b;
}

.bulk-result-summary-card--success {
  border-left-color: #4caf50;
}

.bulk-result-summary-card--issues {
  border-left-color: #ffa92a;
}

.bulk-result-summary-card__value {
  font-size: 36px;
  font-weight: 700;
  line-height: 1.1;
}

.bulk-result-summary-card__value--total {
  color: #607d8b;
}

.bulk-result-summary-card__value--success {
  color: #4caf50;
}

.bulk-result-summary-card__value--issues {
  color: #ffa92a;
}

.bulk-result-summary-card__label {
  color: #757575;
  font-size: 14px;
  margin-top: 4px;
}

.bulk-result-filter-chips {
  gap: 12px;
}

.bulk-result-filter-chip {
  display: inline-flex;
  align-items: center;
  border: none;
  border-radius: 999px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  background-color: #eeeeee;
  color: #424242;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.bulk-result-filter-chip:hover {
  background-color: #e0e0e0;
}

.bulk-result-filter-chip--active.bulk-result-filter-chip--all {
  background-color: #607d8b;
  color: #fff;
}

.bulk-result-filter-chip--active.bulk-result-filter-chip--success {
  background-color: #4caf50;
  color: #fff;
}

.bulk-result-filter-chip--active:not(.bulk-result-filter-chip--all):not(.bulk-result-filter-chip--success) {
  background-color: #ffa92a;
  color: #fff;
}

.bulk-result-filter-chip--active .bulk-result-filter-chip__icon {
  color: #fff !important;
}

.bulk-result-issues-alert {
  background-color: #fff8e6;
  border: 1px solid #ffa92a;
  border-radius: 8px;
  padding: 20px 24px;
}

.bulk-result-issues-alert__heading {
  font-weight: 700;
  color: #212121;
  font-size: 15px;
}

.bulk-result-issues-alert__body {
  color: #424242;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 0;
}

.bulk-result-issues-alert__prompt {
  color: #424242;
  font-size: 14px;
  line-height: 1.5;
}

.bulk-result-bulk-resolution-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.bulk-result-bulk-resolution-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0;
  cursor: pointer;
}

.bulk-result-bulk-resolution-option input {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin: 2px 0 0;
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid #00bd73;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}

.bulk-result-bulk-resolution-option input:checked {
  border-color: #00bd73;
  background-color: #fff;
  background-image: radial-gradient(circle, #00bd73 0 42%, transparent 48%);
}

.bulk-result-bulk-resolution-options--kenya .bulk-result-bulk-resolution-option input {
  border-color: #a75300;
}

.bulk-result-bulk-resolution-options--kenya .bulk-result-bulk-resolution-option input:checked {
  border-color: #a75300;
  background-image: radial-gradient(circle, #a75300 0 42%, transparent 48%);
}

.bulk-result-bulk-actions-row {
  padding-right: 4px;
}

.bulk-result-bulk-cancel-btn--kenya {
  border-color: #a75300 !important;
  color: #a75300 !important;
}

.bulk-result-bulk-continue-btn--kenya {
  background-color: #a75300 !important;
}

.bulk-result-bulk-resolution-option__title {
  font-weight: 700;
  font-size: 14px;
  color: #212121;
  line-height: 1.4;
}

.bulk-result-bulk-resolution-option__body {
  font-size: 13px;
  color: #616161;
  line-height: 1.45;
}

.bulk-result-table-card {
  max-height: 520px;
  overflow: auto;
}

.bulk-result-table ::v-deep thead th {
  background-color: #1b5e20 !important;
}

.bulk-result-table ::v-deep thead th span {
  color: #fff !important;
  text-transform: uppercase;
  font-weight: 600;
}

.bulk-result-table ::v-deep tbody tr:nth-child(even) {
  background-color: #fafafa;
}

.bulk-result-status-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}

.bulk-result-table ::v-deep .green-button {
  background-color: #e8f5e8 !important;
  border: 2px solid #2e7d32 !important;
  color: #2e7d32 !important;
}

.bulk-result-table ::v-deep .orange-button {
  background-color: #fff7ec !important;
  border: 2px solid #ffa826 !important;
  color: #ffa826 !important;
}

.bulk-result-table ::v-deep .red-button {
  background-color: #ffe6e6 !important;
  border: 2px solid #d32f2f !important;
  color: #d32f2f !important;
}

.non-clickable {
  pointer-events: none;
  cursor: default;
}

.bulk-result-table ::v-deep .bulk-result-action-col {
  white-space: nowrap;
}

.bulk-result-table ::v-deep td:last-child {
  white-space: nowrap;
}

.bulk-result-table--clickable ::v-deep tbody tr {
  cursor: pointer;
}

.bulk-result-table--clickable ::v-deep tbody tr:hover {
  background-color: #f5f5f5;
}

.bulk-result-actions {
  gap: 4px;
}

.bulk-result-grower-cell {
  line-height: 1.35;
  white-space: normal;
}

.bulk-result-field--error,
.bulk-result-grower-cell.bulk-result-field--error .text-body-2,
.bulk-result-grower-cell.bulk-result-field--error > span,
.bulk-result-weights-cell.bulk-result-field--error .text-body-2,
.bulk-result-lot-outturn-cell .bulk-result-field--error {
  color: #d32f2f;
}

.bulk-result-issue-tooltip {
  max-width: 340px;
  line-height: 1.45;
  font-size: 13px;
}
</style>

